/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Skeleton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  InputAdornment,
  Alert,
} from "@mui/material";
import { AttachMoney as AttachMoneyIcon } from "@mui/icons-material";
import icon_check from "@assets/img/check_black.svg";
import { ButtonTypeSelectedCustom } from "@components/ButtonTypeSelectedCustom";
import * as yup from "yup";
import { useFormik } from "formik";
import { customStylesAsterisk } from "@components/customStylesInputs/inputsStyles";
import { StyledSlider } from "@components/CustomSlider";
import { formatCurrency } from "@helpers/funciones";
import useInterfaceConfig from "@hooks/useInterfaceConfig";
import { ApiError, CarVersionPrice, LoanCondiciones, TaxSystem, VehicleStrutureCatalog } from "@interfaces/index";
import {
  useLazyGetPriceVehiclesByVersionQuery,
  useLazyGetVehiclesByYearAndMakeQuery,
  useLazyGetVehiclesByYearMakeAndModelQuery,
  useLazyGetVehiclesByYearQuery,
  useLazyGetVehiclesYearsQuery,
} from "@api/catalogs";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@store/store";
import { LeasingPost } from "@interfaces/LeasingTypes";
import { useNavigate } from "react-router-dom";
import { usePostLeasingMutation } from "@api/leasing";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { usePostValidateNextStageMutation, usePutConditionMutation } from "@api/applications";
import * as Sentry from "@sentry/react";
import { setLoanStatus } from "@store/slices/registerSlice";
import useStatusConfig from "@hooks/useStatusConfig";

interface MarketStructure {
  value: number;
  label: string;
}

const SCREEN_STATUS = "CSDVE";
interface SingleFormByOneVehicleProps {
  isLoading?: boolean;
  termMarket?: number[];
  residualPercentageMarket?: MarketStructure[];
  advancePercentageMarket?: MarketStructure[];
  taxConfig?: TaxSystem | null;
  maxAmoutSelected?: number;
  conditions: LoanCondiciones | null;
}

const FormByOneVehicle: React.FC<SingleFormByOneVehicleProps> = ({
  isLoading,
  termMarket,
  residualPercentageMarket,
  taxConfig,
  maxAmoutSelected,
  advancePercentageMarket,
  conditions,
}) => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const interfaceConfig = useInterfaceConfig();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { nextStatus } = useStatusConfig(SCREEN_STATUS);

  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);

  const [triggerListVehicleYears] = useLazyGetVehiclesYearsQuery();
  const [triggerGetVehicleByYear] = useLazyGetVehiclesByYearQuery();
  const [triggerGetVehicleByYearAndBrand] = useLazyGetVehiclesByYearAndMakeQuery();
  const [triggerGetVehicleByYearBrandAndModel] = useLazyGetVehiclesByYearMakeAndModelQuery();
  const [triggerGetPriceVehiclesByVersion] = useLazyGetPriceVehiclesByVersionQuery();
  const [postLeasing] = usePostLeasingMutation();
  const [putCondition] = usePutConditionMutation();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();

  const isShortScreen = useMediaQuery("(max-height:820px)");
  const [vehicleType, setVehicleType] = useState<string>("cars");
  const [selectedTerm, setSelectedTerm] = useState<number | null>(null);
  const [selectedAdvance, setSelectedAdvance] = useState<number>(20);
  const [selectedResidual, setSelectedResidual] = useState<number>(0);
  const [configResidual, setConfigResidual] = useState({
    min: 10,
    max: 35,
    step: 5,
    default: 20,
  });

  const [configAdvance, setConfigAdvance] = useState({
    min: 10,
    max: 35,
    step: 5,
    default: 20,
  });

  const [listYears, setListYears] = useState<string[]>([]);
  const [brandsOptions, setBrandsOptions] = useState<VehicleStrutureCatalog[]>([]);
  const [modelsOptions, setModelsOptions] = useState<VehicleStrutureCatalog[]>([]);
  const [versionsOptions, setVersionsOptions] = useState<VehicleStrutureCatalog[]>([]);
  const [isLoadingForm, setIsLoadingForm] = useState(false);

  const [vehicleValue, setVehicleValue] = useState<number>(0);
  const [vehicleValueDetail, setVehicleValueDetail] = useState<CarVersionPrice | null>(null);
  const [displayPrice, setDisplayPrice] = useState<string>("");
  const [submitError, setSubmitError] = useState<string>("");

  const formik = useFormik({
    initialValues: {
      type_vehicle: "",
      year: "",
      brand: "",
      brand_value: "",
      model: "",
      model_value: "",
      version: "",
      version_value: "",
      term: null,
      advance: null,
      residual: null,
      price: null,
    },
    validationSchema: yup.object().shape({
      year: yup.string().required("Campo requerido"),
      brand: yup.string().required("Campo requerido"),
      model: yup.string().required("Campo requerido"),
      version: yup.string().required("Campo requerido"),
      term: yup.number().required("Campo requerido").min(1, "Selecciona un plazo válido"),
      advance: yup.number().required("Campo requerido"),
      residual: yup.number().required("Campo requerido"),
      price: yup.number().required("Campo requerido"),
    }),
    onSubmit: async (values) => {
      try {
        // Validar contra el monto máximo seleccionado
        const max = Number(maxAmoutSelected ?? 0);
        const priceNum = Number(values.price ?? 0);
        if (max > 0 && priceNum > max) {
          setSubmitError(
            `El valor del vehículo sobrepasa el monto seleccionado. Monto máximo permitido: ${formatCurrency(max)}`,
          );
          return;
        }

        setSubmitError("");
        setIsLoadingForm(true);

        const advanceValue = round2(priceNum * (selectedAdvance / 100));
        const residualValue = round2(priceNum * (selectedResidual / 100));
        const data = {
          loan_application: application.id,
          loan_amount: conditions?.selected_amount,
          requested_term: values.term || 0,
          selected_amount: conditions?.selected_amount,
          selected_term: values.term || 0,
          requested_amount: conditions?.selected_amount,
          selected_advance_percentage: selectedAdvance,
          selected_residual_percentage: selectedResidual,
          selected_advance_value: advanceValue,
          selected_residual_value: residualValue,
          requested_advance_percentage: selectedAdvance,
          requested_residual_percentage: selectedResidual,
          requested_advance_value: advanceValue,
          requested_residual_value: residualValue,
        };
        await putCondition({ body: data, id: String(conditions?.id) }).unwrap();

        const valuesDetails: LeasingPost = {
          loan_application_id: String(application?.id) || "",
          details: {
            vehicle_type: vehicleType === "cars" ? "Autos" : "Camiones",
            year: Number(values.year),
            make: values.brand_value,
            model: values.model_value,
            version: values.version_value,
            car_version_code: vehicleValueDetail?.car_version_id || 0,
            estimated_mileage: vehicleValueDetail?.mileage || 0,
          },
          quantity: 1,
          requested_advance_percentage: selectedAdvance,
          requested_residual_percentage: selectedResidual,
          estimated_value: priceNum,
        };

        const leasingPost = {
          leasings_list: [valuesDetails],
        };

        await postLeasing({ data: leasingPost, id_solicitud: String(application?.id) }).unwrap();
        await triggerPostNextStage({
          applicationId: application.id,
          statusCode: String(nextStatus?.code || ""),
          username: `TOFU - ${accountUser.email}`,
        }).unwrap();

        dispatch(setLoanStatus(String(nextStatus?.code)));

        navigate(String(nextStatus?.url || ""));
      } catch (error) {
        const apiError = error as ApiError;
        Sentry.captureException(error, {
          extra: {
            component: "DetalleVehiclulosArrendamiento-submit",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: apiError }));
      } finally {
        setIsLoadingForm(false);
      }
    },
  });

  const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

  useEffect(() => {
    formik.setFieldValue("advance", selectedAdvance);
    formik.setFieldValue("residual", selectedResidual);
  }, [selectedAdvance, selectedResidual, taxConfig]);

  useEffect(() => {
    const priceNum = Number(formik.values.price ?? 0);
    if (priceNum > 0) {
      setDisplayPrice(new Intl.NumberFormat("es-MX").format(priceNum));
    } else {
      setDisplayPrice("");
    }
  }, [formik.values.price]);

  const leasingCalculation = useMemo(() => {
    if (!selectedTerm || selectedTerm <= 0) {
      return {
        advanceAmount: 0,
        residualAmount: 0,
        amountToFinance: 0,
        monthlyRent: 0,
      };
    }

    const advanceAmount = vehicleValue * (selectedAdvance / 100);
    const residualAmount = vehicleValue * (selectedResidual / 100);
    const amountToFinance = vehicleValue - advanceAmount;

    const annualRate = interfaceConfig?.interest_rate ?? 0;
    const normalizedAnnualRate = annualRate > 1 ? annualRate / 100 : annualRate;
    const monthlyRate = normalizedAnnualRate / 12;

    const i = monthlyRate;
    const n = selectedTerm;
    const P = amountToFinance;
    const F = residualAmount;

    const monthlyRent = i > 0 ? ((P - F / Math.pow(1 + i, n)) * i) / (1 - Math.pow(1 + i, -n)) : (P - F) / n;

    return {
      advanceAmount,
      residualAmount,
      amountToFinance,
      monthlyRent,
    };
  }, [vehicleValue, selectedAdvance, selectedResidual, selectedTerm, interfaceConfig?.interest_rate]);

  useEffect(() => {
    setConfigResidual({
      min: Number(taxConfig?.min_residual_percentage ?? 10),
      max: Number(taxConfig?.max_residual_percentage ?? 35),
      step: Number(taxConfig?.jump_residual_percentage ?? 5),
      default: Number(taxConfig?.default_residual_percentage ?? 20),
    });

    setConfigAdvance({
      min: Number(taxConfig?.min_advance_percentage ?? 10),
      max: Number(taxConfig?.max_advance_percentage ?? 35),
      step: Number(taxConfig?.jump_advance_percentage ?? 5),
      default: Number(taxConfig?.default_advance_percentage ?? 20),
    });
    setSelectedResidual(taxConfig?.default_residual_percentage ?? 20);
    setSelectedAdvance(taxConfig?.default_advance_percentage ?? 20);
  }, [taxConfig]);

  useEffect(() => {
    const fetchVehicleYears = async () => {
      try {
        const response = await triggerListVehicleYears().unwrap();

        setListYears(response);
      } catch (error) {
        const apiError = error as ApiError;
        dispatch(setModalErrorVisible({ open: true, error: apiError }));
      }
    };
    fetchVehicleYears();
  }, []);

  const handleYearChange = async (e: SelectChangeEvent<string>) => {
    try {
      const { value } = e.target;
      formik.setFieldValue("year", value);

      setIsLoadingForm(true);

      const res = value ? await triggerGetVehicleByYear({ year: value }).unwrap() : [];

      setBrandsOptions(res);
      setModelsOptions([]);
      setVersionsOptions([]);

      // Resetear los valores del formulario relacionados
      formik.setFieldValue("brand", "");
      formik.setFieldValue("brand_value", "");
      formik.setFieldValue("model", "");
      formik.setFieldValue("model_value", "");
      formik.setFieldValue("version", "");
      formik.setFieldValue("version_value", "");
    } catch (error) {
      console.error("Error al cargar marcas:", error);
    } finally {
      setIsLoadingForm(false);
    }
  };

  const handleBrandChange = async (e: SelectChangeEvent<string>) => {
    try {
      setIsLoadingForm(true);
      const { value } = e.target;
      const year = formik.values.year;

      const selectedBrand = brandsOptions.find((b) => b.code === value);
      formik.setFieldValue("brand", value);
      formik.setFieldValue("brand_value", selectedBrand?.name || "");

      const res = value ? await triggerGetVehicleByYearAndBrand({ year, make: value }).unwrap() : [];

      setModelsOptions(res);
      setVersionsOptions([]);

      formik.setFieldValue("model", "");
      formik.setFieldValue("model_value", "");
      formik.setFieldValue("version", "");
      formik.setFieldValue("version_value", "");
    } catch (error) {
      const apiError = error as ApiError;

      dispatch(setModalErrorVisible({ open: true, error: apiError }));
    } finally {
      setIsLoadingForm(false);
    }
  };

  const handleModelChange = async (e: SelectChangeEvent<string>) => {
    try {
      setIsLoadingForm(true);
      const { value } = e.target;
      const year = formik.values.year;
      const brand = formik.values.brand;

      const selectedModel = modelsOptions.find((m) => m.code === value);
      formik.setFieldValue("model", value);
      formik.setFieldValue("model_value", selectedModel?.name || "");

      const res = await triggerGetVehicleByYearBrandAndModel({ year, make: brand, model: value }).unwrap();
      setVersionsOptions(res);

      formik.setFieldValue("version", "");
      formik.setFieldValue("version_value", "");
    } catch (error) {
      const apiError = error as ApiError;

      dispatch(setModalErrorVisible({ open: true, error: apiError }));
    } finally {
      setIsLoadingForm(false);
    }
  };

  const handleVersionChange = async (e: SelectChangeEvent<string>) => {
    try {
      setIsLoadingForm(true);
      const value = e.target.value as string;
      const selectedVersion = versionsOptions.find((b) => b.code === value);
      const response = await triggerGetPriceVehiclesByVersion({ version: value }).unwrap();

      formik.setFieldValue("version", value);
      formik.setFieldValue("version_value", selectedVersion?.name || "");
      setVehicleValueDetail(response);
      const max = Number(maxAmoutSelected ?? 0);
      const sale = Number(response.sale_price ?? 0);
      if (max > 0 && sale > max) {
        setSubmitError(
          `El valor del vehículo sobrepasa el monto seleccionado. Monto máximo permitido: ${formatCurrency(max)}`,
        );
      } else {
        setSubmitError("");
      }

      setVehicleValue(response.sale_price);
      formik.setFieldValue("price", response.sale_price);
    } catch (error) {
      const apiError = error as ApiError;

      dispatch(setModalErrorVisible({ open: true, error: apiError }));
    } finally {
      setIsLoadingForm(false);
    }
  };

  return (
    <>
      <Typography textAlign={"center"} variant={"body1"} fontWeight={400} color={theme?.palette?.primary?.dark} mt={4}>
        Selecciona el tipo de vehículo
      </Typography>

      <Grid container spacing={isMobileOrTablet ? 2 : 2} mt={1} mr={isMobileOrTablet ? 1 : 0}>
        <Grid item xs={6} md={6} display="flex" justifyContent={"flex-end"}>
          {!isLoading ? (
            <ButtonTypeSelectedCustom
              title="Autos o SUVs"
              onClick={() => setVehicleType("cars")}
              isSelected={vehicleType === "cars"}
              isMovile={isMobileOrTablet}
              height={isShortScreen ? "60px" : "70px"}
              heightMoviles="60px"
              width={isShortScreen ? "180px" : "190px"}
              widthMoviles="160px"
              icon={icon_check}
              width_icon="22px"
              height_icon="22px"
              font_size={isShortScreen ? "14px" : "14px"}
              top_icon="-12px"
              font_size_movil="11px"
              font_weight={600}
              bgColor={theme?.palette?.primary?.base}
              borderColor={theme?.palette?.primary?.light}
              bgColorActive={theme?.palette?.primary?.light}
              hoverBgColor={theme?.palette?.primary?.light}
              hoverTextColor={theme?.palette?.primary?.dark}
              textColor={theme?.palette?.primary?.dark}
              textColorActive={theme?.palette?.primary?.dark}
            />
          ) : (
            <Skeleton
              variant="rectangular"
              animation="wave"
              width={isMobileOrTablet ? 150 : 190}
              height={isMobileOrTablet ? 60 : 70}
            />
          )}
        </Grid>
        <Grid item xs={6} md={6} display="flex" justifyContent={"flex-start"}>
          {!isLoading ? (
            <ButtonTypeSelectedCustom
              title="Vans o camiones"
              onClick={() => setVehicleType("trucks")}
              isSelected={vehicleType === "trucks"}
              isMovile={isMobileOrTablet}
              height={isShortScreen ? "60px" : "70px"}
              heightMoviles="60px"
              width={isShortScreen ? "180px" : "200px"}
              widthMoviles="160px"
              icon={icon_check}
              width_icon="22px"
              height_icon="22px"
              font_size={isShortScreen ? "14px" : "14px"}
              top_icon="-12px"
              font_size_movil="11px"
              font_weight={600}
              bgColor={theme?.palette?.primary?.base}
              borderColor={theme?.palette?.primary?.light}
              bgColorActive={theme?.palette?.primary?.light}
              hoverBgColor={theme?.palette?.primary?.light}
              hoverTextColor={theme?.palette?.primary?.dark}
              textColor={theme?.palette?.primary?.dark}
              textColorActive={theme?.palette?.primary?.dark}
            />
          ) : (
            <Skeleton
              variant="rectangular"
              animation="wave"
              width={isMobileOrTablet ? 150 : 190}
              height={isMobileOrTablet ? 60 : 70}
            />
          )}
        </Grid>
      </Grid>
      <Box>
        <Typography
          textAlign={"center"}
          variant={"body1"}
          fontWeight={400}
          color={theme?.palette?.primary?.dark}
          mt={4}
        >
          Selecciona el vehículo
        </Typography>
      </Box>

      <Box mt={1} mx={isMobileOrTablet ? 4 : 6}>
        {isLoading ? (
          <>
            <Skeleton variant="rectangular" animation="wave" width={"100%"} height={56} />
            <Box sx={{ mt: 2 }}>
              <Skeleton variant="rectangular" animation="wave" width={"100%"} height={56} />
            </Box>
            <Box sx={{ mt: 2 }}>
              <Skeleton variant="rectangular" animation="wave" width={"100%"} height={56} />
            </Box>
            <Box sx={{ mt: 2 }}>
              <Skeleton variant="rectangular" animation="wave" width={"100%"} height={56} />
            </Box>
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Skeleton variant="rectangular" animation="wave" width={160} height={40} />
            </Box>
          </>
        ) : (
          <>
            <form onSubmit={formik.handleSubmit} noValidate>
              {vehicleType === "trucks" ? (
                <>
                  <TextField
                    fullWidth
                    label="Año"
                    name="year"
                    value={formik.values.year}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d{0,4}$/.test(value)) {
                        formik.handleChange(e);
                      }
                    }}
                    onBlur={formik.handleBlur}
                    id="year"
                    margin="normal"
                    required
                    autoComplete="off"
                    error={formik.touched.year && Boolean(formik.errors.year)}
                    helperText={formik.touched.year && formik.errors.year}
                    inputProps={{ maxLength: 4 }}
                    disabled={formik.isSubmitting}
                  />
                </>
              ) : (
                <>
                  {isLoadingForm ? (
                    <Box sx={{ mt: 2 }}>
                      <Skeleton variant="rectangular" animation="wave" width={"100%"} height={56} />
                    </Box>
                  ) : (
                    <FormControl
                      fullWidth
                      required
                      sx={{
                        ...customStylesAsterisk,
                        mt: 2,
                      }}
                      error={formik.touched.year && Boolean(formik.errors.year)}
                    >
                      <InputLabel id="year">Año</InputLabel>
                      <Select
                        labelId="year"
                        id="year"
                        name="year"
                        fullWidth
                        required
                        label="Año"
                        value={formik.values.year}
                        onBlur={formik.handleBlur}
                        onChange={(e) => handleYearChange(e)}
                        error={formik.touched.year && Boolean(formik.errors.year)}
                        disabled={formik.isSubmitting}
                      >
                        <MenuItem value={""}>Selecciona</MenuItem>
                        {listYears?.map((year, index) => (
                          <MenuItem key={index} value={year}>
                            {year}
                          </MenuItem>
                        ))}
                      </Select>
                      {formik.touched.year && formik.errors.year && (
                        <FormHelperText>{formik.errors.year}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                </>
              )}

              {vehicleType === "trucks" ? (
                <>
                  <TextField
                    fullWidth
                    label="Marca"
                    name="brand"
                    value={formik.values.brand}
                    onChange={(e) => {
                      const value = e.target.value;
                      formik.setFieldValue(`brand`, value);
                      formik.setFieldValue(`brand_value`, value);
                    }}
                    onBlur={formik.handleBlur}
                    id="brand"
                    margin="normal"
                    required
                    autoComplete="off"
                    error={formik.touched.brand && Boolean(formik.errors.brand)}
                    helperText={formik.touched.brand && formik.errors.brand}
                    disabled={formik.isSubmitting}
                  />
                </>
              ) : (
                <>
                  {isLoadingForm ? (
                    <Box sx={{ mt: 2 }}>
                      <Skeleton variant="rectangular" animation="wave" width={"100%"} height={56} />
                    </Box>
                  ) : (
                    <FormControl
                      fullWidth
                      required
                      sx={{
                        ...customStylesAsterisk,
                        mt: 2,
                      }}
                      error={formik.touched.brand && Boolean(formik.errors.brand)}
                    >
                      <InputLabel id="brand">Marca</InputLabel>
                      <Select
                        labelId="brand"
                        id="brand"
                        name="brand"
                        fullWidth
                        required
                        label="Marca"
                        value={formik.values.brand}
                        onBlur={formik.handleBlur}
                        onChange={(e) => handleBrandChange(e)}
                        error={formik.touched.brand && Boolean(formik.errors.brand)}
                        disabled={formik.isSubmitting}
                      >
                        <MenuItem value={""}>Selecciona</MenuItem>
                        {brandsOptions?.map((brand, index) => (
                          <MenuItem key={index} value={brand.code}>
                            {brand.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {formik.touched.brand && formik.errors.brand && (
                        <FormHelperText>{formik.errors.brand}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                </>
              )}

              {vehicleType === "trucks" ? (
                <>
                  <TextField
                    fullWidth
                    label="Modelo"
                    name="model"
                    value={formik.values.model}
                    onChange={(e) => {
                      const value = e.target.value;
                      formik.setFieldValue(`model`, value);
                      formik.setFieldValue(`model_value`, value);
                    }}
                    onBlur={formik.handleBlur}
                    id="model"
                    margin="normal"
                    required
                    autoComplete="off"
                    error={formik.touched.model && Boolean(formik.errors.model)}
                    helperText={formik.touched.model && formik.errors.model}
                    disabled={formik.isSubmitting}
                  />
                </>
              ) : (
                <>
                  {isLoadingForm ? (
                    <Box sx={{ mt: 2 }}>
                      <Skeleton variant="rectangular" animation="wave" width={"100%"} height={56} />
                    </Box>
                  ) : (
                    <FormControl
                      fullWidth
                      required
                      sx={{
                        ...customStylesAsterisk,
                        mt: 2,
                      }}
                      error={formik.touched.model && Boolean(formik.errors.model)}
                    >
                      <InputLabel id="model">Modelo</InputLabel>
                      <Select
                        labelId="model"
                        id="model"
                        name="model"
                        fullWidth
                        required
                        label="Modelo"
                        value={formik.values.model}
                        onBlur={formik.handleBlur}
                        onChange={(e) => handleModelChange(e)}
                        error={formik.touched.model && Boolean(formik.errors.model)}
                        disabled={formik.isSubmitting}
                      >
                        <MenuItem value={""}>Selecciona</MenuItem>
                        {modelsOptions?.map((model, index) => (
                          <MenuItem key={index} value={model.code}>
                            {model.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {formik.touched.model && formik.errors.model && (
                        <FormHelperText>{formik.errors.model}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                </>
              )}

              {vehicleType === "trucks" ? (
                <>
                  <TextField
                    fullWidth
                    label="Versión"
                    name="version"
                    value={formik.values.version}
                    onChange={(e) => {
                      const value = e.target.value;
                      formik.setFieldValue(`version`, value);
                      formik.setFieldValue(`version_value`, value);
                    }}
                    onBlur={formik.handleBlur}
                    id="version"
                    margin="normal"
                    required
                    autoComplete="off"
                    error={formik.touched.version && Boolean(formik.errors.version)}
                    helperText={formik.touched.version && formik.errors.version}
                    disabled={formik.isSubmitting}
                  />
                </>
              ) : (
                <>
                  {isLoadingForm ? (
                    <Box sx={{ mt: 2 }}>
                      <Skeleton variant="rectangular" animation="wave" width={"100%"} height={56} />
                    </Box>
                  ) : (
                    <FormControl
                      fullWidth
                      required
                      sx={{
                        ...customStylesAsterisk,
                        mt: 2,
                      }}
                      error={formik.touched.version && Boolean(formik.errors.version)}
                    >
                      <InputLabel id="version">Versión</InputLabel>
                      <Select
                        labelId="version"
                        id="version"
                        name="version"
                        fullWidth
                        required
                        label="Versión"
                        value={formik.values.version}
                        onBlur={formik.handleBlur}
                        onChange={(e) => handleVersionChange(e)}
                        error={formik.touched.version && Boolean(formik.errors.version)}
                        disabled={formik.isSubmitting}
                      >
                        <MenuItem value={""}>Selecciona</MenuItem>
                        {versionsOptions?.map((version, index) => (
                          <MenuItem key={index} value={version.code}>
                            {version.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {formik.touched.version && formik.errors.version && (
                        <FormHelperText>{formik.errors.version}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                </>
              )}

              {vehicleType === "trucks" && (
                <>
                  <TextField
                    fullWidth
                    label="Valor del vehículo"
                    name="price"
                    value={displayPrice}
                    onChange={(e) => {
                      if (submitError) setSubmitError("");
                      const raw = (e.target.value || "").replace(/[^\d]/g, "");
                      if (raw.length <= 10) {
                        const num = raw ? Number(raw) : 0;
                        setDisplayPrice(num ? new Intl.NumberFormat("es-MX").format(num) : "");
                        formik.setFieldValue("price", num || null);
                        setVehicleValue(num);
                      }
                    }}
                    onBlur={formik.handleBlur}
                    id="price"
                    margin="normal"
                    required
                    autoComplete="off"
                    error={formik.touched.price && Boolean(formik.errors.price)}
                    helperText={formik.touched.price && formik.errors.price}
                    disabled={formik.isSubmitting}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AttachMoneyIcon fontSize="small" />
                        </InputAdornment>
                      ),
                      inputMode: "numeric",
                    }}
                    inputProps={{ maxLength: 11 }}
                  />
                </>
              )}

              {submitError && (
                <Box mt={2}>
                  <Alert severity="error">{submitError}</Alert>
                </Box>
              )}

              <Typography
                textAlign={"center"}
                variant={"body1"}
                fontWeight={400}
                color={theme?.palette?.primary?.dark}
                mt={4}
              >
                Confirma el plazo en meses
              </Typography>

              <Box
                display={"flex"}
                justifyContent={"center"}
                gap={1}
                mt={2}
                flexWrap={"wrap"}
                sx={{
                  "& > *": { marginX: 0.5 },
                }}
              >
                {isLoadingForm ? (
                  <Skeleton variant="rectangular" animation="wave" width={isMobileOrTablet ? 38.5 : 45} height={35} />
                ) : (
                  <>
                    {(termMarket ?? []).map((term) => (
                      <ButtonTypeSelectedCustom
                        key={term}
                        title={String(term)}
                        onClick={() => {
                          formik.setFieldValue("term", term);
                          setSelectedTerm(term);
                        }}
                        isSelected={selectedTerm === term}
                        isMovile={isMobileOrTablet}
                        height={"35px"}
                        heightMoviles="35px"
                        width="38.5px"
                        widthMoviles="38.5px"
                        font_size="11px"
                        font_size_movil="11px"
                        font_weight={500}
                        bgColor={theme?.palette?.primary?.base}
                        borderColor={theme?.palette?.primary?.light}
                        bgColorActive={theme?.palette?.primary?.light}
                        hoverBgColor={theme?.palette?.primary?.light}
                        hoverTextColor={theme?.palette?.primary?.dark}
                        textColor={theme?.palette?.primary?.dark}
                        textColorActive={theme?.palette?.primary?.dark}
                      />
                    ))}
                  </>
                )}
              </Box>

              <Typography
                textAlign={"center"}
                variant={"body1"}
                fontWeight={400}
                color={theme?.palette?.primary?.dark}
                mt={2}
              >
                Confirmar el anticipo
              </Typography>

              <Box mt={1} mb={2}>
                <StyledSlider
                  aria-label="amount"
                  min={configAdvance.min}
                  max={configAdvance.max}
                  step={configAdvance.step}
                  marks={advancePercentageMarket}
                  defaultValue={configAdvance.default}
                  value={selectedAdvance}
                  onChange={(_event, newValue) => setSelectedAdvance(newValue as number)}
                  disabled={isLoadingForm}
                />
              </Box>

              <Typography
                textAlign={"center"}
                variant={"body1"}
                fontWeight={400}
                color={theme?.palette?.primary?.dark}
                mt={2}
              >
                Confirma el residual
              </Typography>

              <Box mt={1} mb={2}>
                <StyledSlider
                  aria-label="amount"
                  min={configResidual.min}
                  max={configResidual.max}
                  step={configResidual.step}
                  marks={residualPercentageMarket}
                  defaultValue={configResidual.default}
                  value={selectedResidual}
                  onChange={(_event, newValue) => setSelectedResidual(newValue as number)}
                  disabled={isLoadingForm}
                />
              </Box>
              <Box display={"flex"} flexDirection={"row"} mt={4} mb={4}>
                <Box width={"45%"}>
                  <Typography
                    variant={isMobileOrTablet ? "body2" : "body1"}
                    fontWeight={400}
                    textAlign={"center"}
                    color={theme?.palette?.primary?.dark}
                  >
                    Valor del anticipo
                  </Typography>
                  <Typography
                    fontSize={"18px"}
                    fontWeight={600}
                    textAlign={"center"}
                    mt={1}
                    color={theme?.palette?.primary?.main}
                  >
                    {formatCurrency(leasingCalculation.advanceAmount)}{" "}
                    <Typography variant="body3" fontWeight={500}>
                      {interfaceConfig.cotizador?.current_currency || "MXN"}
                    </Typography>
                  </Typography>
                </Box>

                <Box width={"10%"} display={"flex"} justifyContent={"center"} alignItems={"center"}>
                  <Divider orientation="vertical" flexItem sx={{ mr: "-2px", border: "1px solid #F2704F" }} />
                </Box>

                <Box width={"45%"}>
                  <Typography
                    variant={isMobileOrTablet ? "body2" : "body1"}
                    fontWeight={400}
                    textAlign={"center"}
                    color={theme?.palette?.primary?.dark}
                  >
                    Valor del residual
                  </Typography>

                  <Typography
                    fontSize={"18px"}
                    fontWeight={600}
                    textAlign={"center"}
                    color={theme?.palette?.primary?.main}
                    mt={1}
                  >
                    {formatCurrency(leasingCalculation.residualAmount)}{" "}
                    <Typography variant="body3" fontWeight={500}>
                      {interfaceConfig.cotizador?.current_currency || "MXN"}
                    </Typography>
                  </Typography>
                </Box>
              </Box>

              <Box display={"flex"} flexDirection={"row"} mt={4} mb={4}>
                <Box width={"45%"}>
                  <Typography
                    variant={isMobileOrTablet ? "body2" : "body1"}
                    fontWeight={400}
                    textAlign={"center"}
                    color={theme?.palette?.primary?.dark}
                  >
                    Valor del vehículo
                  </Typography>
                  <Typography
                    fontSize={"18px"}
                    fontWeight={600}
                    textAlign={"center"}
                    mt={1}
                    color={theme?.palette?.primary?.main}
                  >
                    {formatCurrency(vehicleValue)}{" "}
                    <Typography variant="body3" fontWeight={500}>
                      {interfaceConfig.cotizador?.current_currency || "MXN"}
                    </Typography>
                  </Typography>
                </Box>

                <Box width={"10%"} display={"flex"} justifyContent={"center"} alignItems={"center"}>
                  <Divider orientation="vertical" flexItem sx={{ mr: "-2px", border: "1px solid #F2704F" }} />
                </Box>

                <Box width={"45%"}>
                  <Typography
                    variant={isMobileOrTablet ? "body2" : "body1"}
                    fontWeight={400}
                    textAlign={"center"}
                    color={theme?.palette?.primary?.dark}
                  >
                    Monto a financiar
                  </Typography>

                  <Typography
                    fontSize={"18px"}
                    fontWeight={600}
                    textAlign={"center"}
                    color={theme?.palette?.primary?.main}
                    mt={1}
                  >
                    {formatCurrency(leasingCalculation.amountToFinance)}{" "}
                    <Typography variant="body3" fontWeight={500}>
                      {interfaceConfig.cotizador?.current_currency || "MXN"}
                    </Typography>
                  </Typography>
                </Box>
              </Box>
              <Box>
                <Typography
                  fontSize={"17px"}
                  fontWeight={700}
                  textAlign={"center"}
                  color={theme?.palette?.primary?.main}
                >
                  Renta mensual estimada
                </Typography>

                <Typography
                  fontSize={"22px"}
                  fontWeight={700}
                  textAlign={"center"}
                  color={theme?.palette?.primary?.dark}
                  mt={1}
                >
                  {formatCurrency(leasingCalculation.monthlyRent || 0)}{" "}
                  <Typography variant="body3" fontWeight={700}>
                    {interfaceConfig.cotizador?.current_currency || "MXN"}
                  </Typography>
                </Typography>

                <Box display={"flex"} justifyContent={"center"} mt={2}>
                  <Button variant="default" disabled={isLoadingForm || !formik.isValid} type="submit">
                    Continuar
                  </Button>
                </Box>
              </Box>
            </form>
          </>
        )}
      </Box>
    </>
  );
};

export default FormByOneVehicle;
